import { memo, type ComponentType } from 'react';
import { IconButton, Stack, Typography, type SvgIconProps, type SxProps, type Theme } from '@mui/material';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import CloseIcon from '@mui/icons-material/Close';

interface FloatingPanelHeaderProps {
  icon: ComponentType<SvgIconProps>;
  title: string;
  /** Used in aria-labels: "Pin {label}", "Unpin {label}", "Close {label}". */
  label: string;
  pinned: boolean;
  onTogglePin: () => void;
  onClose: () => void;
  /** Optional style overrides (e.g. a panel that wants roomier padding). Merged after the defaults. */
  sx?: SxProps<Theme>;
}

function FloatingPanelHeader({
  icon: Icon,
  title,
  label,
  pinned,
  onTogglePin,
  onClose,
  sx,
}: FloatingPanelHeaderProps) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={[
        { px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Stack direction="row" alignItems="center" gap={1}>
        <Icon sx={{ fontSize: 18, color: 'text.primary' }} />
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'text.primary' }}>
          {title}
        </Typography>
      </Stack>
      <Stack direction="row" gap={0.5}>
        <IconButton
          size="small"
          aria-label={pinned ? `Unpin ${label}` : `Pin ${label}`}
          aria-pressed={pinned}
          onClick={onTogglePin}
          sx={{ color: pinned ? 'text.primary' : 'text.secondary' }}
        >
          {pinned ? (
            <PushPinIcon sx={{ fontSize: 16 }} />
          ) : (
            <PushPinOutlinedIcon sx={{ fontSize: 16 }} />
          )}
        </IconButton>
        <IconButton
          size="small"
          aria-label={`Close ${label}`}
          onClick={onClose}
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Stack>
    </Stack>
  );
}

export default memo(FloatingPanelHeader);
